import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    apiGetAllFees, apiGetFeeSummary, apiCreateFee,
    apiGetFeeById, apiRecordPayment, apiDeleteFee,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import CalendarPicker from '../components/CalendarPicker';
import toast from 'react-hot-toast';

const fmt   = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
const fmtD  = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const pct   = (paid, net) => net > 0 ? Math.min(100, Math.round((paid / net) * 100)) : 0;

const STATUS_COLORS = {
    Paid: '#10b981', Partial: '#f59e0b', Pending: '#6366f1', Overdue: '#ef4444',
};
const PAYMENT_STATUSES = { Paid: 'badge--active', Partial: 'badge--followup', Pending: 'badge--new', Overdue: 'badge--danger' };

const emptyFeeForm = {
    lead_id: '', total_amount: '', payment_type: 'full', emi_months: '',
    discount: '', start_date: '', remarks: '',
};

export default function FeesPage() {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [fees, setFees]           = useState([]);
    const [summary, setSummary]     = useState(null);
    const [loading, setLoading]     = useState(true);
    const [search, setSearch]       = useState('');
    const [statusFilter, setStatus] = useState('');

    // Modals
    const [createModal, setCreateModal] = useState(false);
    const [detailModal, setDetailModal] = useState(null); // {fee, payments}
    const [payModal, setPayModal]       = useState(null); // {feeId, payment}
    const [feeForm, setFeeForm]         = useState(emptyFeeForm);
    const [payForm, setPayForm]         = useState({ amount: '', paid_date: '', payment_mode: 'Cash', receipt_number: '', notes: '' });
    const [saving, setSaving]           = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    // Converted leads for the create dropdown
    const [convertedLeads, setConvertedLeads] = useState([]);

    const loadSummary = useCallback(async () => {
        try { const r = await apiGetFeeSummary(); setSummary(r.data?.data); } catch {}
    }, []);

    const loadFees = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiGetAllFees();
            setFees(r.data?.data || []);
        } catch { toast.error('Failed to load fees'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        loadFees();
        loadSummary();
        // Load converted leads for create dropdown
        import('../services/api').then(({ apiGetLeads }) => {
            apiGetLeads({ status: 'Converted', limit: 200 })
                .then(r => setConvertedLeads(r.data?.data || []))
                .catch(() => {});
        });
    }, [loadFees, loadSummary]);

    const openDetail = async (feeId) => {
        setDetailLoading(true);
        try {
            const r = await apiGetFeeById(feeId);
            setDetailModal(r.data?.data);
        } catch { toast.error('Failed to load details'); }
        finally { setDetailLoading(false); }
    };

    // Create fee
    const handleCreateFee = async (e) => {
        e.preventDefault();
        if (!feeForm.lead_id) return toast.error('Select a student');
        if (!feeForm.total_amount || parseFloat(feeForm.total_amount) <= 0) return toast.error('Enter total amount');
        if (!feeForm.start_date) return toast.error('Select start date');
        if (feeForm.payment_type === 'emi' && (!feeForm.emi_months || parseInt(feeForm.emi_months) < 2))
            return toast.error('EMI months must be at least 2');
        setSaving(true);
        try {
            await apiCreateFee({
                ...feeForm,
                total_amount: parseFloat(feeForm.total_amount),
                discount: parseFloat(feeForm.discount) || 0,
                emi_months: feeForm.payment_type === 'emi' ? parseInt(feeForm.emi_months) : null,
            });
            toast.success('Fee record created with payment schedule!');
            setCreateModal(false);
            setFeeForm(emptyFeeForm);
            loadFees();
            loadSummary();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Create failed');
        } finally { setSaving(false); }
    };

    // Record payment
    const handleRecordPayment = async (e) => {
        e.preventDefault();
        if (!payForm.paid_date) return toast.error('Enter payment date');
        if (!payForm.amount || parseFloat(payForm.amount) <= 0) return toast.error('Enter amount');
        setSaving(true);
        try {
            await apiRecordPayment(payModal.feeId, {
                payment_id: payModal.payment.id,
                amount: parseFloat(payForm.amount),
                paid_date: payForm.paid_date,
                payment_mode: payForm.payment_mode,
                receipt_number: payForm.receipt_number,
                notes: payForm.notes,
            });
            toast.success('Payment recorded!');
            setPayModal(null);
            setPayForm({ amount: '', paid_date: '', payment_mode: 'Cash', receipt_number: '', notes: '' });
            // Refresh detail modal
            if (detailModal) {
                const r = await apiGetFeeById(detailModal.id);
                setDetailModal(r.data?.data);
            }
            loadFees();
            loadSummary();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Payment failed');
        } finally { setSaving(false); }
    };

    const handleDeleteFee = async (id) => {
        if (!window.confirm('Delete this fee record and all payments?')) return;
        try {
            await apiDeleteFee(id);
            toast.success('Fee record deleted');
            setDetailModal(null);
            loadFees();
            loadSummary();
        } catch { toast.error('Delete failed'); }
    };

    const filtered = fees.filter(f => {
        const matchSearch = !search || f.student_name?.toLowerCase().includes(search.toLowerCase()) || f.student_phone?.includes(search);
        const matchStatus = !statusFilter || f.status === statusFilter;
        return matchSearch && matchStatus;
    });

    // Net amount for the create form preview
    const previewNet = parseFloat(feeForm.total_amount || 0) - parseFloat(feeForm.discount || 0);
    const previewEmi = feeForm.payment_type === 'emi' && feeForm.emi_months
        ? (previewNet / parseInt(feeForm.emi_months)).toFixed(2)
        : null;

    return (
        <div className="page">
            {/* ─── Header ─────────────────────────────────────────────── */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">💰 Student Fees</h1>
                    <p className="page-subtitle">Track course fees, EMI schedules and payment records</p>
                </div>
                <button id="create-fee-btn" className="btn btn-primary" onClick={() => setCreateModal(true)}>
                    ➕ Add Fee Record
                </button>
            </div>

            {/* ─── Summary Cards ──────────────────────────────────────────── */}
            {summary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                    {[
                        { label: 'Total Students', value: summary.total_students, color: '#6366f1', icon: '🎓' },
                        { label: 'Total Fees', value: fmt(summary.total_fees), color: '#3b82f6', icon: '📋' },
                        { label: 'Collected', value: fmt(summary.total_collected), color: '#10b981', icon: '✅' },
                        { label: 'Pending', value: fmt(summary.total_pending), color: '#f59e0b', icon: '⏳' },
                        { label: 'Fully Paid', value: summary.fully_paid, color: '#10b981', icon: '🏅' },
                        { label: 'Overdue', value: summary.overdue_count, color: '#ef4444', icon: '🔴' },
                    ].map(s => (
                        <div key={s.label} className="card" style={{ padding: '16px', textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                            <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>{s.icon}</div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px', fontWeight: 500 }}>{s.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* ─── Filters ──────────────────────────────────────────────── */}
            <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '12px', padding: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                        placeholder="🔍 Search by name or phone…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ flex: 1, minWidth: 200 }}
                    />
                    <select value={statusFilter} onChange={e => setStatus(e.target.value)} style={{ width: 160 }}>
                        <option value="">All Status</option>
                        <option>Pending</option>
                        <option>Partial</option>
                        <option>Paid</option>
                        <option>Overdue</option>
                    </select>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setStatus(''); }}>Clear</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { loadFees(); loadSummary(); }}>🔄 Refresh</button>
                </div>
            </div>

            {/* ─── Fees Table ──────────────────────────────────────────── */}
            {loading ? (
                <div className="loading-center"><div className="spinner" /><span>Loading…</span></div>
            ) : filtered.length === 0 ? (
                <div className="card"><div className="empty-state">
                    <div className="empty-state-icon">💰</div>
                    <p>No fee records yet. Add converted students' fee details.</p>
                    <button className="btn btn-primary" onClick={() => setCreateModal(true)}>➕ Add Fee Record</button>
                </div></div>
            ) : (
                <div className="card">
                    <div className="table-wrapper">
                        <table className="data-table" id="fees-table">
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th>Course</th>
                                    <th>Total Fees</th>
                                    <th>Paid</th>
                                    <th>Due</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Progress</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(f => {
                                    const net  = parseFloat(f.net_amount || 0);
                                    const paid = parseFloat(f.paid_amount || 0);
                                    const due  = net - paid;
                                    const prog = pct(paid, net);
                                    return (
                                        <tr key={f.id} id={`fee-row-${f.id}`}>
                                            <td>
                                                <div>
                                                    <strong
                                                        style={{ color: 'var(--accent-light)', cursor: 'pointer' }}
                                                        onClick={() => navigate(`/leads/${f.lead_id}`)}
                                                    >{f.student_name}</strong>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{f.student_phone}</div>
                                                </div>
                                            </td>
                                            <td style={{ fontSize: '0.82rem' }}>{f.course}</td>
                                            <td style={{ fontWeight: 600 }}>{fmt(net)}</td>
                                            <td style={{ color: '#10b981', fontWeight: 600 }}>{fmt(paid)}</td>
                                            <td style={{ color: due > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>{fmt(due)}</td>
                                            <td>
                                                <span style={{ fontSize: '0.78rem', padding: '3px 10px', background: f.payment_type === 'emi' ? 'rgba(99,102,241,0.15)' : 'rgba(16,185,129,0.12)', color: f.payment_type === 'emi' ? 'var(--accent-light)' : '#10b981', borderRadius: 20, fontWeight: 600 }}>
                                                    {f.payment_type === 'emi' ? `EMI (${f.emi_months}m)` : 'Full'}
                                                </span>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: '0.78rem', padding: '3px 10px', background: STATUS_COLORS[f.status] + '22', color: STATUS_COLORS[f.status], borderRadius: 20, fontWeight: 600 }}>
                                                    {f.status}
                                                </span>
                                            </td>
                                            <td style={{ minWidth: 100 }}>
                                                <div style={{ background: 'var(--bg-base)', borderRadius: 6, height: 6, overflow: 'hidden' }}>
                                                    <div style={{ width: `${prog}%`, height: '100%', background: STATUS_COLORS[f.status], borderRadius: 6, transition: 'width 0.4s' }} />
                                                </div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 3 }}>{prog}%</div>
                                            </td>
                                            <td>
                                                <div className="action-cell">
                                                    <button
                                                        id={`view-fee-${f.id}`}
                                                        className="btn btn-ghost btn-sm"
                                                        onClick={() => openDetail(f.id)}
                                                        disabled={detailLoading}
                                                    >📋 Details</button>
                                                    {user?.role === 'admin' && (
                                                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDeleteFee(f.id)}>🗑</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ──────────────────────────────────────────────────────────────────────
                CREATE FEE MODAL
            ────────────────────────────────────────────────────────────────────── */}
            {createModal && (
                <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCreateModal(false)}>
                    <div className="modal" style={{ maxWidth: 560 }}>
                        <div className="modal-header">
                            <h2 className="modal-title">💰 Add Fee Record</h2>
                            <button className="modal-close" onClick={() => setCreateModal(false)}>✕</button>
                        </div>
                        <form onSubmit={handleCreateFee}>
                            <div className="modal-body">
                                {/* Student Select */}
                                <div className="form-group">
                                    <label htmlFor="fee-student">Converted Student *</label>
                                    <select
                                        id="fee-student" value={feeForm.lead_id}
                                        onChange={e => setFeeForm(f => ({ ...f, lead_id: e.target.value }))}
                                    >
                                        <option value="">— Select Student —</option>
                                        {convertedLeads.map(l => (
                                            <option key={l.id} value={l.id}>{l.name} · {l.phone} · {l.course}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="fee-total">Total Course Fees (₹) *</label>
                                        <input id="fee-total" type="number" min="0" placeholder="e.g. 25000"
                                            value={feeForm.total_amount}
                                            onChange={e => setFeeForm(f => ({ ...f, total_amount: e.target.value }))} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="fee-discount">Discount (₹)</label>
                                        <input id="fee-discount" type="number" min="0" placeholder="0"
                                            value={feeForm.discount}
                                            onChange={e => setFeeForm(f => ({ ...f, discount: e.target.value }))} />
                                    </div>
                                </div>

                                {/* Net preview */}
                                {feeForm.total_amount && (
                                    <div style={{ background: 'var(--accent-dim)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: '0.85rem' }}>
                                        <span>Net Amount: <strong style={{ color: 'var(--accent-light)' }}>{fmt(previewNet)}</strong></span>
                                        {previewEmi && <span>Per EMI: <strong style={{ color: 'var(--accent-light)' }}>{fmt(previewEmi)}</strong></span>}
                                    </div>
                                )}

                                {/* Payment Type */}
                                <div className="form-group">
                                    <label>Payment Type *</label>
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        {['full', 'emi'].map(t => (
                                            <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 18px', background: feeForm.payment_type === t ? 'var(--accent-dim)' : 'var(--bg-base)', border: `1px solid ${feeForm.payment_type === t ? 'var(--border-focus)' : 'var(--border)'}`, borderRadius: 'var(--radius)', fontSize: '0.875rem', fontWeight: 600, color: feeForm.payment_type === t ? 'var(--accent-light)' : 'var(--text-secondary)' }}>
                                                <input type="radio" name="payment_type" value={t} style={{ display: 'none' }} checked={feeForm.payment_type === t} onChange={() => setFeeForm(f => ({ ...f, payment_type: t, emi_months: '' }))} />
                                                {t === 'full' ? '💵 Full Payment' : '📆 EMI (Monthly)'}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {feeForm.payment_type === 'emi' && (
                                    <div className="form-group">
                                        <label htmlFor="fee-emi">Number of EMI Months *</label>
                                        <input id="fee-emi" type="number" min="2" max="36" placeholder="e.g. 6 months"
                                            value={feeForm.emi_months}
                                            onChange={e => setFeeForm(f => ({ ...f, emi_months: e.target.value }))} />
                                        <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Monthly installments will be auto-generated from the start date</small>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>Start Date *</label>
                                    <CalendarPicker
                                        id="fee-start-date"
                                        value={feeForm.start_date}
                                        onChange={d => setFeeForm(f => ({ ...f, start_date: d }))}
                                        placeholder="📅 Select payment start date"
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label htmlFor="fee-remarks">Remarks</label>
                                    <input id="fee-remarks" placeholder="Optional notes…"
                                        value={feeForm.remarks}
                                        onChange={e => setFeeForm(f => ({ ...f, remarks: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-ghost" onClick={() => setCreateModal(false)}>Cancel</button>
                                <button id="create-fee-submit" type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</> : '💾 Create Fee Record'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ──────────────────────────────────────────────────────────────────────
                DETAIL / EMI SCHEDULE MODAL
            ────────────────────────────────────────────────────────────────────── */}
            {detailModal && (
                <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetailModal(null)}>
                    <div className="modal" style={{ maxWidth: 680 }}>
                        <div className="modal-header">
                            <div>
                                <h2 className="modal-title">📋 {detailModal.student_name}</h2>
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 2 }}>{detailModal.course} · {detailModal.student_phone}</p>
                            </div>
                            <button className="modal-close" onClick={() => setDetailModal(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            {/* Fee Summary Strip */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                                {[
                                    { label: 'Total Fees', value: fmt(detailModal.net_amount), color: '#6366f1' },
                                    { label: 'Paid', value: fmt(detailModal.paid_amount), color: '#10b981' },
                                    { label: 'Remaining', value: fmt(parseFloat(detailModal.net_amount) - parseFloat(detailModal.paid_amount)), color: parseFloat(detailModal.paid_amount) >= parseFloat(detailModal.net_amount) ? '#10b981' : '#ef4444' },
                                    { label: 'Status', value: detailModal.status, color: STATUS_COLORS[detailModal.status] },
                                ].map(s => (
                                    <div key={s.label} style={{ background: 'var(--bg-base)', borderRadius: 'var(--radius)', padding: '12px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 3 }}>{s.label}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Progress Bar */}
                            {(() => {
                                const p = pct(detailModal.paid_amount, detailModal.net_amount);
                                return (
                                    <div style={{ marginBottom: 20 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                                            <span>Collection Progress</span><span>{p}%</span>
                                        </div>
                                        <div style={{ background: 'var(--bg-base)', borderRadius: 8, height: 10, overflow: 'hidden' }}>
                                            <div style={{ width: `${p}%`, height: '100%', background: `linear-gradient(90deg, var(--accent), #10b981)`, borderRadius: 8, transition: 'width 0.5s' }} />
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* EMI/Payment Schedule Table */}
                            <div style={{ marginBottom: 8 }}>
                                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: 10 }}>
                                    {detailModal.payment_type === 'emi' ? `📆 EMI Schedule (${detailModal.emi_months} months)` : '💵 Payment Record'}
                                </div>
                                <div className="table-wrapper" style={{ maxHeight: 320, overflowY: 'auto' }}>
                                    <table className="data-table" id="emi-schedule-table">
                                        <thead>
                                            <tr>
                                                {detailModal.payment_type === 'emi' && <th>EMI #</th>}
                                                <th>Amount</th>
                                                <th>Scheduled</th>
                                                <th>Paid On</th>
                                                <th>Mode</th>
                                                <th>Receipt</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(detailModal.payments || []).map(p => (
                                                <tr key={p.id} id={`payment-row-${p.id}`} style={{ opacity: p.status === 'Paid' ? 0.75 : 1 }}>
                                                    {detailModal.payment_type === 'emi' && <td style={{ fontWeight: 700 }}>#{p.emi_number}</td>}
                                                    <td style={{ fontWeight: 600 }}>{fmt(p.amount)}</td>
                                                    <td style={{ whiteSpace: 'nowrap', color: p.status === 'Overdue' ? '#ef4444' : 'var(--text-secondary)' }}>{fmtD(p.scheduled_date)}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{p.paid_date ? fmtD(p.paid_date) : '—'}</td>
                                                    <td style={{ fontSize: '0.78rem' }}>{p.payment_mode || '—'}</td>
                                                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{p.receipt_number || '—'}</td>
                                                    <td>
                                                        <span style={{ fontSize: '0.72rem', padding: '3px 9px', background: STATUS_COLORS[p.status] + '22', color: STATUS_COLORS[p.status], borderRadius: 20, fontWeight: 700 }}>
                                                            {p.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {p.status !== 'Paid' && (
                                                            <button
                                                                id={`pay-btn-${p.id}`}
                                                                className="btn btn-success btn-sm"
                                                                onClick={() => {
                                                                    setPayModal({ feeId: detailModal.id, payment: p });
                                                                    setPayForm({ amount: p.amount, paid_date: new Date().toISOString().split('T')[0], payment_mode: 'Cash', receipt_number: '', notes: '' });
                                                                }}
                                                            >✔ Pay</button>
                                                        )}
                                                        {p.status === 'Paid' && <span style={{ color: '#10b981', fontSize: '0.8rem' }}>✅ Done</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {detailModal.remarks && (
                                <div style={{ background: 'var(--bg-base)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: '0.83rem', color: 'var(--text-muted)', marginTop: 12 }}>
                                    📝 {detailModal.remarks}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/leads/${detailModal.lead_id}`)}>👁 View Lead</button>
                            {user?.role === 'admin' && (
                                <button className="btn btn-danger btn-sm" onClick={() => handleDeleteFee(detailModal.id)}>🗑 Delete Record</button>
                            )}
                            <button className="btn btn-ghost" onClick={() => setDetailModal(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ──────────────────────────────────────────────────────────────────────
                RECORD PAYMENT MODAL
            ────────────────────────────────────────────────────────────────────── */}
            {payModal && (
                <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPayModal(null)}>
                    <div className="modal" style={{ maxWidth: 420 }}>
                        <div className="modal-header">
                            <h2 className="modal-title">✔ Record Payment {payModal.payment.emi_number ? `— EMI #${payModal.payment.emi_number}` : ''}</h2>
                            <button className="modal-close" onClick={() => setPayModal(null)}>✕</button>
                        </div>
                        <form onSubmit={handleRecordPayment}>
                            <div className="modal-body">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="pay-amount">Amount (₹) *</label>
                                        <input id="pay-amount" type="number" min="1" value={payForm.amount}
                                            onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="pay-mode">Payment Mode</label>
                                        <select id="pay-mode" value={payForm.payment_mode}
                                            onChange={e => setPayForm(f => ({ ...f, payment_mode: e.target.value }))}>
                                            <option>Cash</option>
                                            <option>UPI</option>
                                            <option>Online Transfer</option>
                                            <option>Cheque</option>
                                            <option>Card</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Payment Date *</label>
                                    <CalendarPicker
                                        id="pay-date"
                                        value={payForm.paid_date}
                                        onChange={d => setPayForm(f => ({ ...f, paid_date: d }))}
                                        placeholder="📅 Select payment date"
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="pay-receipt">Receipt No.</label>
                                        <input id="pay-receipt" placeholder="e.g. RCP-001" value={payForm.receipt_number}
                                            onChange={e => setPayForm(f => ({ ...f, receipt_number: e.target.value }))} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="pay-notes">Notes</label>
                                        <input id="pay-notes" placeholder="Optional…" value={payForm.notes}
                                            onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-ghost" onClick={() => setPayModal(null)}>Cancel</button>
                                <button id="confirm-payment-btn" type="submit" className="btn btn-success" disabled={saving}>
                                    {saving ? '…' : '✔ Confirm Payment'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
